import {
  STRATEGY_LABELS,
  type StatusResponse,
  type StrategyKey,
} from "../lib/types";
import {
  ageLabel,
  ageMinutes,
  dollarsCompact,
  minutesUntil,
  pct,
  untilLabel,
} from "../lib/format";

const HEALTH_COLOR: Record<string, string> = {
  ok: "bg-accent-ok text-emerald-950",
  error: "bg-accent-err text-red-50",
  paused: "bg-accent-warn text-amber-950",
  market_closed: "bg-accent-info text-blue-50",
  skipped: "bg-accent-warn/60 text-amber-950",
  deferred: "bg-sky-700/40 text-sky-200",  // info, not warning — operator-friendly
  missing: "bg-accent-mute text-slate-100",
};

const HEALTH_LABEL: Record<string, string> = {
  ok: "OK",
  error: "ERROR",
  paused: "PAUSED",
  market_closed: "MKT CLOSED",
  skipped: "SKIPPED",
  deferred: "ARMED",  // friendlier framing — the strategy IS active, just gated
  missing: "NO BEAT",
};

/** Human-readable explanation of a status. Hover-tooltip on the badge. */
const HEALTH_EXPLAIN: Record<string, string> = {
  ok: "Last routine succeeded",
  error: "Last routine errored — check logs",
  paused: "Strategy paused (PAUSED.md exists)",
  market_closed: "Skipped — market closed",
  skipped: "Skipped — preflight returned exit 2",
  deferred: "Routine ran but is intentionally waiting (e.g., equity-history floor)",
  missing: "No heartbeat received yet",
};

// 8 hours — the longest gap between two routines in the bot schedule.
const STALE_MIN = 8 * 60;

type Props = {
  strategy: StrategyKey;
  block: StatusResponse["strategies"][StrategyKey];
  onOpen?: (s: StrategyKey) => void;
};

// Note shown on cards is capped to keep the card compact. Longer notes
// are visible on hover (`title` attribute) and on the strategy detail
// page. Anything longer than this is truncated with ellipsis.
const NOTE_PREVIEW_CHARS = 100;

export function StrategyCard({ strategy, block, onOpen }: Props) {
  const hb = block.heartbeat;
  const nav = block.nav_latest;
  const dt = block.decisions_today;

  const ageMin = ageMinutes(hb?.last_run_at_utc ?? null);
  const stale = ageMin !== null && ageMin > STALE_MIN;
  const status: string = stale ? "error" : (hb?.status ?? "missing");
  const nextMin = minutesUntil(hb?.next_expected_at_utc ?? null);

  // Highlight the note for states where it carries an explanation
  // the operator actually needs to read (deferred = "why is this not
  // trading?", error = "what failed?"). For ok/healthy states the note
  // is fine to stay subdued.
  const noteIsActionable = status === "deferred" || status === "error";

  return (
    <button
      onClick={() => onOpen?.(strategy)}
      className="group relative flex flex-col gap-3 rounded-lg border border-ink-700 bg-ink-900 p-4 text-left transition hover:border-ink-600 hover:bg-ink-800 focus:outline-none focus:ring-2 focus:ring-accent-info"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="truncate text-lg font-semibold tracking-wide text-slate-50">
          {STRATEGY_LABELS[strategy]}
        </h3>
        <span
          className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${HEALTH_COLOR[status] ?? HEALTH_COLOR.missing}`}
          title={HEALTH_EXPLAIN[status] ?? ""}
        >
          {HEALTH_LABEL[status] ?? "?"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">
            NAV
          </div>
          <div className="font-mono text-base text-slate-100">
            {dollarsCompact(nav?.nav_cents ?? null)}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">
            Decisions today
          </div>
          <div className="font-mono text-base text-slate-100">
            <span className="text-accent-ok">{dt.allowed}</span>
            <span className="px-1 text-slate-600">/</span>
            <span className="text-accent-err">{dt.denied}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-ink-700 pt-2 text-xs text-slate-400">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">
            {hb ? hb.source_routine : <span className="italic">no routine yet</span>}
          </span>
          <span
            className={`shrink-0 ${stale ? "font-semibold text-accent-err" : ""}`}
          >
            {ageLabel(ageMin)}
          </span>
        </div>
        {hb?.note && (
          <div
            className={
              noteIsActionable
                ? "mt-1.5 rounded bg-ink-800/50 px-2 py-1 text-xs text-sky-300 leading-snug"
                : "mt-1 truncate text-slate-500"
            }
            title={hb.note}
          >
            {noteIsActionable && hb.note.length > NOTE_PREVIEW_CHARS
              ? `${hb.note.slice(0, NOTE_PREVIEW_CHARS)}…`
              : hb.note}
          </div>
        )}
        {nextMin !== null && (
          <div className="mt-1 text-xs text-slate-500">
            next run <span className="font-mono">{untilLabel(nextMin)}</span>
          </div>
        )}
      </div>

      {block.nav_latest && (
        <div className="text-xs text-slate-500">
          last close{" "}
          <span className="font-mono">{block.nav_latest.trading_day_et}</span>{" "}
          • SPY {pct(null)}{/* SPY edge is computed on detail page */}
        </div>
      )}
    </button>
  );
}
